import { datadogRum } from '@datadog/browser-rum'
import debug from '../utils/console'
import { isDebugEnabled } from '../utils/debug/isDebugEnabled'
import { watchReadinessMilestones } from '../utils/ad-ops/readinessMilestones'
import type { ReadinessSample } from '../utils/ad-ops/readinessMilestones'
import {
  activationStart,
  isAnotherPage,
  isPrerendering,
  pageStart,
} from '../utils/rumPage'

const logger = debug.withTag('readiness-milestones')

/**
 * Sends the ad stack readiness milestones (`gpt_ready`, `ay_ready`) to RUM.
 * See utils/ad-ops/readinessMilestones.ts.
 *
 * The clock starts when the visitor started seeing the page: the start of the
 * navigation, or the moment a prerendered page was shown. RUM starts later, from
 * the tenant's app.vue, and the SDK holds these calls until it does. Nothing is
 * sent when the tenant has no RUM configured, and a page that loaded in a
 * background tab reports nothing, since its timings would be about the visitor
 * and not about the page. Milestones reached after the tab went to the
 * background or the visitor left for another path are dropped too.
 */
export default defineNuxtPlugin({
  name: 'readiness-milestones',
  setup() {
    const config = useRuntimeConfig()
    if (
      !config.public.datadogRumApplicationId ||
      !config.public.datadogRumClientToken
    ) {
      return
    }
    if (document.visibilityState === 'hidden' && !isPrerendering()) return

    const debugEnabled = isDebugEnabled()
    // Only a measurement, so a failure here must never take the page down.
    try {
      const report = ({ name, duration }: ReadinessSample) => {
        const options = { startTime: pageStart(), duration }
        if (debugEnabled) logger.info('Readiness milestone', name, options)
        datadogRum.addDurationVital(name, options)
      }

      const milestones = watchReadinessMilestones({
        pageStart: activationStart,
        report,
      })

      const stopWhenHidden = () => {
        if (document.visibilityState === 'hidden' && !isPrerendering()) {
          milestones.stop()
          document.removeEventListener('visibilitychange', stopWhenHidden)
        }
      }
      document.addEventListener('visibilitychange', stopWhenHidden)

      // A query change (a category tab) is the same page. Another path is not.
      const removeRouteHook = useRouter().afterEach((to, from) => {
        if (isAnotherPage(to, from)) {
          milestones.stop()
          removeRouteHook()
        }
      })
    } catch (error) {
      logger.warn('Could not start sending readiness milestones', error)
    }
  },
})
